import { IconCopy } from '@developerz/ui';
import { A } from '@solidjs/router';
import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import { copyText } from '../lib/clipboard';
import { shortId } from '../lib/fleet-machines';
import type { UtilBox } from '../lib/fleetUtilization';
import { runningJobs } from '../lib/machine-jobs';
import { MACHINE_HEALTH, MACHINE_STATUS, serverText, taskLine } from '../lib/machine-labels';
import { orgHref } from '../lib/org';
import { ago } from '../lib/time';
import { Button, buttonClass } from './ui/Button';

// The top of the machine details panel (#6563, prototype `panel()` head): the copy-link
// control, `<8-char id> · <server>`, status, verdict and last seen, then what it is working
// on (its oldest running job, #6745; the card lists every job) with "Watch it live", or
// what it is doing instead. The name is the drawer's heading.
//
// THE DOT IS THE ONE LIVE SURFACE, as on the card: a working machine wears `.status__dot`
// (and so does "Watch it live", which only a running task offers); every other state a
// neutral or red dot. Healthy is neutral, never green.

export function MachinePanelHead(props: {
  readonly box: UtilBox;
  readonly notify: (message: string, tone?: 'success' | 'error') => void;
}): JSX.Element {
  const health = () => MACHINE_HEALTH[props.box.verdict.health];
  const working = (): boolean => props.box.verdict.status === 'working';
  const copyLink = async (): Promise<void> => {
    const ok = await copyText(window.location.href);
    props.notify(
      t(ok ? 'fleet.panel.link_copied' : 'fleet.panel.link_copy_failed'),
      ok ? 'success' : 'error',
    );
  };

  return (
    <div class="machine-panel__head">
      <div class="machine-panel__bar">
        <span class="machine-panel__crumb">{t('fleet.panel.crumb')}</span>
        <Button
          variant="outline"
          size="icon-sm"
          aria-label={t('fleet.panel.copy_link')}
          onClick={() => void copyLink()}
        >
          <IconCopy size="1rem" />
        </Button>
      </div>
      <p class="machine-panel__sub">
        <span>{shortId(props.box.id)}</span>
        <Show when={serverText(props.box)}>{(server) => <span>{server()}</span>}</Show>
      </p>
      <Show when={props.box.via_org}>
        {(org) => <p class="machine-panel__from">{t('fleet.machines.from_org', { org: org() })}</p>}
      </Show>
      <div class="machine-panel__state">
        <span class="machine-panel__status" data-status={props.box.verdict.status}>
          <Show
            when={working()}
            fallback={
              <span
                class="machine-card__dot"
                data-status={props.box.verdict.status}
                aria-hidden="true"
              />
            }
          >
            <span class="status__dot" aria-hidden="true" />
          </Show>
          {t(MACHINE_STATUS[props.box.verdict.status])}
        </span>
        <span class={`fleet-verdict fleet-verdict--${health().tone}`}>{t(health().key)}</span>
        <span class="machine-panel__seen">
          {props.box.last_beat_iso === null
            ? t('fleet.panel.last_seen_never')
            : t('fleet.panel.last_seen', { when: ago(props.box.last_beat_iso) })}
        </span>
      </div>
      <Show
        when={working() ? (runningJobs(props.box)[0] ?? null) : null}
        fallback={<p class="machine-panel__idle">{taskLine(props.box, t)}</p>}
      >
        {(task) => (
          <div class="machine-panel__task">
            <div class="machine-panel__task-text">
              <span class="machine-panel__label">{t('fleet.panel.working_on')}</span>
              <span class="machine-panel__task-title">
                {task().title ?? taskLine(props.box, t)}
              </span>
              <span class="machine-panel__task-sub">
                <Show when={task().repo}>{(repo) => <span>{repo()}</span>}</Show>
                <Show when={task().started_at}>
                  {(at) => <span>{t('fleet.panel.started', { when: ago(at()) })}</span>}
                </Show>
              </span>
            </div>
            <Show when={task().run_id}>
              {(run) => (
                <A
                  class={`${buttonClass({ variant: 'outline', size: 'sm' })} machine-panel__watch`}
                  href={orgHref(`/fleet/runs/${encodeURIComponent(run())}`)}
                >
                  <span class="status__dot" aria-hidden="true" />
                  {t('fleet.panel.watch_live')}
                </A>
              )}
            </Show>
          </div>
        )}
      </Show>
    </div>
  );
}
